import {
  Alert,
  Badge,
  Button,
  Card,
  Container,
  Group,
  Modal,
  Progress,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { Practitioner, Task, TaskOutput } from '@medplum/fhirtypes';
import { IconPlus } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { UnifyTable, type UnifyTableColumn } from '@/components/UnifyTable';
import { Loading } from '../../components/Loading';
import { TASK_OUTPUT_SYSTEM } from '@unify/fhir-model/constants';
import { getOutputAssignmentDisposition } from '../../fhir-model/resources/referral-task';
import { formatUsDateTime } from '@/lib/dateHelpers';
import { LocalhostOnlyBadge } from '@/components/LocalhostOnlyBadge';
import type { ReferralUiStatusLabel } from '../../lib/referralRequestsFhir';
import { ReferralRequestCreateModal } from './ReferralRequestCreateModal';
import {
  type ReferralTaskRow,
  taskPreferencesFromRow,
  useAdminReferralTasks,
} from './useAdminReferralTasks';

function getStatusColor(status: ReferralTaskRow['status']): string {
  const colorMap: Record<ReferralTaskRow['status'], string> = {
    Pending: 'gray',
    'In Progress': 'blue',
    Completed: 'green',
    Cancelled: 'red',
  };
  return colorMap[status] ?? 'gray';
}

function getFillPercentage(sent: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((sent / total) * 100);
}

function isAssignedEpisodeOutput(o: TaskOutput): boolean {
  return Boolean(
    o.type?.coding?.some((c) => c.system === TASK_OUTPUT_SYSTEM && c.code === 'assigned-episode'),
  );
}

export function ReferralRequestsPage(): JSX.Element {
  const {
    rows,
    loading,
    error,
    showPracticeColumn,
    providerOptions,
    providers,
    statusFilter,
    setStatusFilter,
    refetch,
    createReferralRequest,
    cancelReferralRequest,
  } = useAdminReferralTasks();

  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [viewedRequest, setViewedRequest] = useState<ReferralTaskRow | null>(null);
  const providersById = useMemo((): Record<string, Practitioner | undefined> => {
    const map: Record<string, Practitioner | undefined> = {};
    for (const provider of providers) {
      if (provider.id) map[provider.id] = provider;
    }
    return map;
  }, [providers]);

  const columns = useMemo((): UnifyTableColumn<ReferralTaskRow>[] => {
    const cols: UnifyTableColumn<ReferralTaskRow>[] = [
      {
        key: 'requestingProvider',
        label: 'Provider',
        getValue: (r) =>
          [r.requestingProvider, r.practice, r.status, r.id, ...r.clientNames].filter(Boolean).join(' '),
        render: (r) => <Text size="sm">{r.requestingProvider}</Text>,
      },
    ];
    if (showPracticeColumn) {
      cols.push({
        key: 'practice',
        label: 'Practice',
        getValue: (r) => r.practice ?? '',
        render: (r) => <Text size="sm">{r.practice ?? '—'}</Text>,
      });
    }
    cols.push(
      {
        key: 'requestDate',
        label: 'Request Date',
        getValue: (r) => r.requestDate,
        render: (r) => <Text size="sm">{formatUsDateTime(r.requestDate) || '—'}</Text>,
      },
      {
        key: 'numberOfClients',
        label: 'Slots Requested',
        getValue: (r) => String(r.numberOfClients),
        render: (r) => <Text size="sm">{r.numberOfClients}</Text>,
      },
      {
        key: 'fillProgress',
        label: 'Fill Progress',
        sortable: false,
        getValue: (r) =>
          `${Math.min(r.referralsPending + r.referralsConverted, r.numberOfClients)}/${r.numberOfClients}`,
        render: (r) => {
          const total = r.numberOfClients;
          const committed = Math.min(r.referralsPending + r.referralsConverted, total);
          const reserved = Math.min(r.referralsReserved, Math.max(0, total - committed));
          const committedPct = getFillPercentage(committed, total);
          const inProgressPct = getFillPercentage(reserved, total);
          return (
            <Stack gap={4}>
              <Text size="sm" c="dimmed">
                {committed} / {total} filled
                {reserved > 0 && ` · ${reserved} in progress`}
              </Text>
              <Progress.Root size="sm">
                <Progress.Section value={committedPct} color={committed >= total ? 'green' : 'blue'} />
                {reserved > 0 && <Progress.Section value={inProgressPct} color="yellow" />}
              </Progress.Root>
            </Stack>
          );
        },
      },
      {
        key: 'status',
        label: 'Status',
        getValue: (r) => r.status,
        render: (r) => (
          <Badge color={getStatusColor(r.status)} variant="light">
            {r.status}
          </Badge>
        ),
      },
      {
        key: 'lastUpdateDate',
        label: 'Last assigned',
        getValue: (r) => r.lastUpdateDate,
        render: (r) => <Text size="sm">{formatUsDateTime(r.lastUpdateDate) || '—'}</Text>,
      },
    );
    return cols;
  }, [showPracticeColumn]);

  const handleOpenRequestModal = () => {
    setRequestModalOpen(true);
  };

  const handleCancelRequest = () => {
    if (!viewedRequest) return;
    void (async () => {
      try {
        await cancelReferralRequest(viewedRequest.sourceTask);
        notifications.show({
          title: viewedRequest.referralsReserved > 0 ? 'Cancellation queued' : 'Request cancelled',
          message:
            viewedRequest.referralsReserved > 0
              ? 'Cancellation will complete when the in-progress intake reservation clears.'
              : 'The referral request was cancelled.',
          color: 'teal',
        });
        setViewedRequest(null);
      } catch (e) {
        console.error(e);
        notifications.show({
          title: 'Could not cancel',
          message: e instanceof Error ? e.message : 'Unknown error',
          color: 'red',
        });
      }
    })();
  };

  const detailTask: Task | null = viewedRequest?.sourceTask ?? null;
  const prefs = detailTask ? taskPreferencesFromRow(detailTask) : null;
  const assignmentOutputs =
    detailTask?.output?.filter((o) => isAssignedEpisodeOutput(o)) ?? [];

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start">
          <div>
            <Group gap="sm" align="center" wrap="wrap">
              <Title order={2}>Referral Requests</Title>
              <LocalhostOnlyBadge />
            </Group>
            <Text c="dimmed" size="sm">
              View and manage provider capacity requests
            </Text>
          </div>
          <Button leftSection={<IconPlus size={16} />} onClick={handleOpenRequestModal}>
            Request Referrals
          </Button>
        </Group>

        {error && (
          <Alert color="red" title="Could not load referral requests">
            {error}{' '}
            <Button variant="light" size="xs" onClick={() => void refetch()}>
              Retry
            </Button>
          </Alert>
        )}

        <Card withBorder>
          {loading && rows.length === 0 ? <Loading fullScreen={false} /> : null}
          {loading && rows.length === 0 ? null : (
            <UnifyTable
              withPaper={false}
              columns={columns}
              data={rows}
              getRowKey={(r) => r.id}
              searchable
              searchPlaceholder="Search by provider..."
              sortable
              sortableColumnKeys={[
                'requestingProvider',
                ...(showPracticeColumn ? ['practice'] : []),
                'requestDate',
                'numberOfClients',
                'status',
                'lastUpdateDate',
              ]}
              pagination
              defaultRowsPerPage={25}
              multiFilter={{
                columnKey: 'status',
                allLabel: 'All Statuses',
                serverFiltered: true,
                defaultValue: statusFilter,
                data: [
                  { value: 'Pending', label: 'Pending' },
                  { value: 'In Progress', label: 'In Progress' },
                  { value: 'Completed', label: 'Completed' },
                  { value: 'Cancelled', label: 'Cancelled' },
                ],
              }}
              onMultiFilterChange={(values) => {
                setStatusFilter(values as ReferralUiStatusLabel[]);
              }}
              onRowClick={setViewedRequest}
            />
          )}
        </Card>
      </Stack>

      <ReferralRequestCreateModal
        opened={requestModalOpen}
        onClose={() => setRequestModalOpen(false)}
        providerOptions={providerOptions}
        providersById={providersById}
        onSubmit={async (values) => {
          try {
            await createReferralRequest(values);
            notifications.show({
              title: 'Referral request created',
              message: 'The capacity request was saved.',
              color: 'green',
            });
            setRequestModalOpen(false);
          } catch (e) {
            console.error(e);
            notifications.show({
              title: 'Could not create request',
              message: e instanceof Error ? e.message : 'Unknown error',
              color: 'red',
            });
          }
        }}
      />

      <Modal
        opened={viewedRequest !== null}
        onClose={() => setViewedRequest(null)}
        title="Request details"
        size="lg"
        centered
      >
        {viewedRequest && detailTask && prefs && (
          <Stack gap="lg">
            {viewedRequest.cancellationQueued && (
              <Alert color="yellow" title="Cancellation pending">
                A client is completing intake on a reserved slot. This request will cancel automatically when the
                reservation clears.
              </Alert>
            )}
            <Group grow>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Provider
                </Text>
                <Text size="sm" fw={500}>
                  {viewedRequest.requestingProvider}
                </Text>
              </div>
              {showPracticeColumn && (
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Practice
                  </Text>
                  <Text size="sm" fw={500}>
                    {viewedRequest.practice ?? '—'}
                  </Text>
                </div>
              )}
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Requested
                </Text>
                <Text size="sm" fw={500}>
                  {formatUsDateTime(viewedRequest.requestDate) || '—'}
                </Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Slots requested
                </Text>
                <Text size="sm" fw={500}>
                  {viewedRequest.numberOfClients}
                </Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Status
                </Text>
                <Badge color={getStatusColor(viewedRequest.status)} variant="light" size="sm">
                  {viewedRequest.status}
                </Badge>
              </div>
            </Group>

            <Stack gap="xs">
              <Text size="sm" fw={600}>
                Preferences
              </Text>
              <Group gap="xs">
                {prefs.acceptsInPerson && (
                  <Badge variant="light" color="gray" size="sm">
                    In-person
                  </Badge>
                )}
                {prefs.noMinors && (
                  <Badge variant="light" color="gray" size="sm">
                    No minors
                  </Badge>
                )}
                {prefs.noCouplesFamilies && (
                  <Badge variant="light" color="gray" size="sm">
                    No couples/families
                  </Badge>
                )}
                {prefs.onlyCashPay && (
                  <Badge variant="light" color="gray" size="sm">
                    Cash-pay only
                  </Badge>
                )}
              </Group>
            </Stack>

            <div>
              <Text size="sm" fw={600} mb="xs">
                Assignments
              </Text>
              {assignmentOutputs.length === 0 ? (
                <Text size="sm" c="dimmed">
                  No episode assignments yet.
                </Text>
              ) : (
                <Table highlightOnHover withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Episode</Table.Th>
                      <Table.Th>Disposition</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {assignmentOutputs.map((o, idx) => {
                      const d = getOutputAssignmentDisposition(o) ?? 'pending';
                      const label =
                        d === 'converted'
                          ? 'Converted'
                          : d === 'declined'
                            ? 'Declined'
                            : d === 'reserved'
                              ? 'Reserved'
                              : 'Pending';
                      const color =
                        d === 'converted'
                          ? 'green'
                          : d === 'declined'
                            ? 'red'
                            : d === 'reserved'
                              ? 'yellow'
                              : 'gray';
                      return (
                        <Table.Tr key={`${viewedRequest.id}-out-${idx}`}>
                          <Table.Td>
                            <Text size="sm">
                              {o.valueReference?.display ?? o.valueReference?.reference ?? '—'}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Badge variant="light" color={color} size="sm">
                              {label}
                            </Badge>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              )}
            </div>

            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={() => setViewedRequest(null)}>
                Close
              </Button>
              <Button
                color="red"
                onClick={handleCancelRequest}
                disabled={viewedRequest.status === 'Cancelled' || viewedRequest.status === 'Completed'}
              >
                Cancel Request
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Container>
  );
}
